import { computed, Injectable, signal, Signal } from '@angular/core';
import { User } from '@auth0/auth0-spa-js';
import { Client, ClientProviderEvents, EvaluationContext, JsonValue, OpenFeature } from '@openfeature/angular-sdk';
import { Project } from 'lfx-pcc';

@Injectable({
  providedIn: 'root',
})
export class FeatureFlagService {
  private client: Client | null = null;
  private readonly isInitialized = signal<boolean>(false);
  private readonly context = signal<EvaluationContext | null>(null);

  private userContext: EvaluationContext | null = null;
  private projectContext: EvaluationContext | null = null;
  private currentContextType = signal<'user' | 'project'>('user');

  // Public readonly signals
  public readonly initialized = this.isInitialized.asReadonly();

  /**
   * Initialize the feature flag service with user context
   */
  public async initialize(user: User): Promise<void> {
    if (this.isInitialized()) {
      return;
    }

    try {
      this.userContext = {
        kind: 'user',
        name: user.name || '',
        email: user.email || '',
        targetingKey: user['https://sso.linuxfoundation.org/claims/username'] || user.sub || '',
      };

      await OpenFeature.setContext(this.userContext);
      this.client = OpenFeature.getClient();
      this.context.set(this.userContext);
      this.currentContextType.set('user');
      this.isInitialized.set(true);

      this.setupEventHandlers();
    } catch (error) {
      console.error('Failed to initialize feature flag service:', error);
      this.isInitialized.set(false);
    }
  }

  /**
   * Set project context for project-specific flags.
   * Automatically switches to project context when set.
   *
   * If called before the service is initialized, this method will log a warning and return
   * without setting the project context.
   */
  public async setProject(project: Project): Promise<void> {
    if (!this.isInitialized()) {
      console.warn('Feature flag service not initialized');
      return;
    }

    try {
      this.projectContext = {
        kind: 'project',
        name: project.Name || '',
        targetingKey: project.ID || '',
        projectId: project.ID,
        projectName: project.Name,
      };

      await OpenFeature.setContext(this.projectContext);
      this.context.set(this.projectContext);
      this.currentContextType.set('project');
    } catch (error) {
      console.error('Failed to set project context:', error);
    }
  }

  /**
   * Switch back to user context
   */
  public async switchToUserContext(): Promise<void> {
    if (!this.isInitialized() || !this.userContext) {
      return;
    }

    try {
      await OpenFeature.setContext(this.userContext);
      this.context.set(this.userContext);
      this.currentContextType.set('user');
    } catch (error) {
      console.error('Failed to switch to user context:', error);
    }
  }

  /**
   * Get a boolean feature flag as a signal
   */
  public getBooleanFlag(flagKey: string, defaultValue: boolean = false): Signal<boolean> {
    return computed(() => {
      // Track context changes
      this.context();

      if (!this.isInitialized() || !this.client) {
        return defaultValue;
      }

      try {
        return this.client.getBooleanValue(flagKey, defaultValue);
      } catch (error) {
        console.error(`Error evaluating boolean flag '${flagKey}':`, error);
        return defaultValue;
      }
    });
  }

  /**
   * Get a string feature flag as a signal
   */
  public getStringFlag(flagKey: string, defaultValue: string = ''): Signal<string> {
    return computed(() => {
      this.context();

      if (!this.isInitialized() || !this.client) {
        return defaultValue;
      }

      try {
        return this.client.getStringValue(flagKey, defaultValue);
      } catch (error) {
        console.error(`Error evaluating string flag '${flagKey}':`, error);
        return defaultValue;
      }
    });
  }

  /**
   * Get a number feature flag as a signal
   */
  public getNumberFlag(flagKey: string, defaultValue: number = 0): Signal<number> {
    return computed(() => {
      this.context();

      if (!this.isInitialized() || !this.client) {
        return defaultValue;
      }

      try {
        return this.client.getNumberValue(flagKey, defaultValue);
      } catch (error) {
        console.error(`Error evaluating number flag '${flagKey}':`, error);
        return defaultValue;
      }
    });
  }

  /**
   * Get an object feature flag as a signal
   */
  public getObjectFlag<T extends JsonValue>(flagKey: string, defaultValue: T): Signal<T> {
    return computed(() => {
      this.context();

      if (!this.isInitialized() || !this.client) {
        return defaultValue;
      }

      try {
        return this.client.getObjectValue(flagKey, defaultValue) as T;
      } catch (error) {
        console.error(`Error evaluating object flag '${flagKey}':`, error);
        return defaultValue;
      }
    });
  }

  /**
   * Force refresh all flags by updating the context signal
   * This triggers re-evaluation of all computed flag signals
   */
  public refreshFlags(): void {
    const current = this.context();
    if (current) {
      this.context.set({ ...current });
    }
  }

  private setupEventHandlers(): void {
    if (!this.client) return;

    const forceSignalUpdate = () => {
      // Force re-evaluation of all computed signals by updating context
      // This creates a new reference which triggers signal re-computation
      this.refreshFlags();
    };

    const handleError = (error: any) => {
      console.error('Feature flag provider error:', error);
    };

    // Set up event handlers for flag changes
    this.client.addHandler(ClientProviderEvents.Ready, forceSignalUpdate);
    this.client.addHandler(ClientProviderEvents.ConfigurationChanged, forceSignalUpdate);
    this.client.addHandler(ClientProviderEvents.ContextChanged, forceSignalUpdate);
    this.client.addHandler(ClientProviderEvents.Reconciling, forceSignalUpdate);
    this.client.addHandler(ClientProviderEvents.Stale, forceSignalUpdate);
    this.client.addHandler(ClientProviderEvents.Error, handleError);

    // Also set up global handlers for cross-cutting concerns
    try {
      OpenFeature.addHandler(ClientProviderEvents.ConfigurationChanged, forceSignalUpdate);
      OpenFeature.addHandler(ClientProviderEvents.ContextChanged, forceSignalUpdate);
    } catch {
      // Global handlers may not be supported in all versions
    }
  }
}
